import { ISettingsProps } from './Models.ts';
import { levelOf } from './FactLevels.ts';

/*
 * Turns the settings a sheet was built from into GA4 event parameters, for the
 * pdf_opened / pdf_saved events in ADR 0026.
 *
 * This is the whole point of 0026 §1: rather than an event per settings change,
 * the configuration rides along on the event that marks the outcome, so "which
 * worksheets actually get printed" is one report instead of a join across two
 * event streams.
 *
 * Kept separate from Shared/analytics.ts, which stays generic - the mapping
 * belongs with the settings it describes, and this way it is testable without a
 * tag and the enum-driven test below it can insist a new problem format shows up
 * here (ADR 0026 §6).
 *
 * GA4 constraints that shape what may go in: values must be primitives, names
 * are capped at 40 characters and string values at 100, and there are at most 25
 * parameters per event. worksheetEventParams.test.ts holds all of that.
 *
 * The seed is deliberately absent. ADR 0026 §3: it would make any printed sheet
 * reproducible from analytics, which costs a custom dimension to answer a
 * question nobody asked and puts an awkward sentence in the privacy policy.
 */

export type EventParams = Record<string, string | number | boolean>;

export function worksheetEventParams(settings: ISettingsProps): EventParams {
    // A sheet can carry several operators; send a stable joined string rather
    // than the SelectOption array, which GA4 would drop and which would reach
    // the tag as "[object Object]" if it were coerced.
    const operation = (settings.Operators ?? [])
        .map((op) => op?.value)
        .filter(Boolean)
        .join(',');

    return {
        // Format values are the display strings ('Rounding & Comparing'), the
        // same vocabulary `fmt=` uses in the URL. One vocabulary, not two.
        format: settings.Format,
        operation,
        problems_per_page: settings.NumberOfProblemsPerPage,
        pages: settings.NumberOfPages,
        half_page: settings.HalfPage,
        answer_pages: settings.AnswerPages,
        differentiation_set: settings.DifferentiationSet,
        // The level a level test is at, or 'none' (ADR 0071). Text, because a
        // level is grouped by, not summed; read it beside `operation`, since
        // level 3 of addition is not level 3 of multiplication. Whether anyone
        // uses levels decides whether an addition page is ever built (§9).
        fact_level: String(levelOf(settings)?.id ?? 'none'),
        // 'test' or 'practice' on a level sheet, else 'none' (ADR 0071 §8): read beside
        // `fact_level`, to see whether the home practice is used at all.
        fact_level_sheet: levelOf(settings) ? settings.FactLevelSheet : 'none',
    };
}

export default worksheetEventParams;
